import { z } from 'zod';
import { IdSchema } from './encoding.js';
/** One level of an account's hierarchy path, L1 to L4 (FRD-FIN-001, FIN-011). */
export const AccountPathSegmentSchema = z.object({
    level: z.string(),
    code: z.string(),
    name: z.string(),
});
/**
 * An account as the list endpoint presents it (`GET /api/v1/ledger/accounts`): its identity, the
 * attributes of the revision in force (FRD-FIN-002) and its hierarchy path, so every list and
 * picker can present the code with the path (FRD-FIN-011).
 */
export const AccountSchema = z.object({
    id: IdSchema,
    code: z.string(),
    name: z.string(),
    accountGroupId: IdSchema,
    /** L1 to L4, root first; derived from the explicit parent links, never from the code. */
    path: z.array(AccountPathSegmentSchema),
    accountType: z.string(),
    financialStatement: z.string(),
    normalBalance: z.string(),
    isPosting: z.boolean(),
    isSystemControlled: z.boolean(),
    reconciliationRequired: z.boolean(),
    vatApplicable: z.boolean(),
    vatTreatment: z.string(),
    moduleTag: z.string(),
    capexOpex: z.string(),
    directIndirect: z.string(),
    status: z.string(),
    effectiveDate: z.string(),
    /** The COA dimension flags in force (FRD-FIN-020): which references a posting line needs. */
    requires: z.object({
        costCenter: z.boolean(),
        project: z.boolean(),
        client: z.boolean(),
        supplier: z.boolean(),
        employee: z.boolean(),
        asset: z.boolean(),
    }),
    version: z.number().int(),
});
/** One account revision (FRD-FIN-017): what the account was, from when, and why it changed. */
export const AccountRevisionSchema = z.object({
    id: IdSchema,
    validFrom: z.string(),
    validTo: z.string().nullable(),
    name: z.string(),
    accountGroupId: IdSchema,
    accountType: z.string(),
    financialStatement: z.string(),
    normalBalance: z.string(),
    isPosting: z.boolean(),
    isSystemControlled: z.boolean(),
    reconciliationRequired: z.boolean(),
    vatApplicable: z.boolean(),
    vatTreatment: z.string(),
    moduleTag: z.string(),
    capexOpex: z.string(),
    directIndirect: z.string(),
    requires: z.object({
        costCenter: z.boolean(),
        project: z.boolean(),
        client: z.boolean(),
        supplier: z.boolean(),
        employee: z.boolean(),
        asset: z.boolean(),
    }),
    status: z.string(),
    effectiveDate: z.string(),
    description: z.string().nullable(),
    remarks: z.string().nullable(),
    changeReason: z.string().nullable(),
    createdBy: IdSchema.nullable(),
});
/** One account with its description, remarks and every revision, newest first. */
export const AccountDetailSchema = z.object({
    account: AccountSchema,
    description: z.string().nullable(),
    remarks: z.string().nullable(),
    revisions: z.array(AccountRevisionSchema),
});
/** An L1 to L4 account group (`GET /api/v1/ledger/account-groups`; FRD-FIN-001, FIN-004). */
export const AccountGroupSchema = z.object({
    id: IdSchema,
    level: z.string(),
    code: z.string(),
    name: z.string(),
    parentGroupId: IdSchema.nullable(),
});
/**
 * An amendment of one account (FRD-FIN-006, FIN-008): the attributes that change and the reason.
 * It creates a new revision; the code never changes (FRD-FIN-010) and history is kept (FIN-017).
 */
export const AmendAccountRequestSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
    name: z.string().trim().min(1).max(200).optional(),
    accountGroupId: IdSchema.optional(),
    accountType: z.string().trim().min(1).max(50).optional(),
    financialStatement: z.enum(['Balance Sheet', 'P&L']).optional(),
    normalBalance: z.enum(['Debit', 'Credit']).optional(),
    isPosting: z.boolean().optional(),
    isSystemControlled: z.boolean().optional(),
    reconciliationRequired: z.boolean().optional(),
    vatApplicable: z.boolean().optional(),
    vatTreatment: z.string().trim().min(1).max(100).optional(),
    moduleTag: z.string().trim().min(1).max(100).optional(),
    capexOpex: z.string().trim().min(1).max(20).optional(),
    directIndirect: z.string().trim().min(1).max(20).optional(),
    requiresCostCenter: z.boolean().optional(),
    requiresProject: z.boolean().optional(),
    requiresClient: z.boolean().optional(),
    requiresSupplier: z.boolean().optional(),
    requiresEmployee: z.boolean().optional(),
    requiresAsset: z.boolean().optional(),
    status: z.enum(['active', 'inactive']).optional(),
    effectiveDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    description: z.string().max(2000).nullable().optional(),
    remarks: z.string().max(2000).nullable().optional(),
});
/** An accounting period (`GET /api/v1/ledger/accounting-periods`). */
export const AccountingPeriodSchema = z.object({
    id: IdSchema,
    code: z.string(),
    status: z.enum(['open', 'closed']),
    periodType: z.enum(['normal', 'opening']),
    startDate: z.string(),
    endDate: z.string(),
    version: z.number().int(),
    fiscalYear: z.number().int(),
    closedAt: z.string().nullable(),
    closedBy: IdSchema.nullable(),
});
/** One period as its page shows it: a reopening already in approval is not offered twice. */
export const AccountingPeriodDetailSchema = AccountingPeriodSchema.extend({
    reopeningPending: z.boolean(),
});
const BusinessDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD');
/** A new accounting period (FRD-FIN-108): opened when created (FRD-FIN-109). */
export const CreateAccountingPeriodRequestSchema = z.strictObject({
    code: z.string().trim().min(1).max(20),
    fiscalYear: z.number().int().min(2000).max(2999),
    startDate: BusinessDate,
    endDate: BusinessDate,
    /** `opening` is the dated period opening balances post to (FRD-FIN-144). */
    periodType: z.enum(['normal', 'opening']).optional(),
});
/** A request to reopen a closed period, with its reason, for the approval route (FRD-FIN-110). */
export const ReopenPeriodRequestSchema = z.strictObject({
    reason: z.string().trim().min(1).max(500),
});
/**
 * COA import contract (FRD-FIN-015, FIN-016; SDD §6.5).
 *
 * One row is **one L5 posting account** carrying its own explicit L1–L4 hierarchy, exactly as the
 * approved COA workbook presents it. The approved workbook's `COA Code` column is not represented:
 * it duplicates `L5 Code` on every row and the ledger model has no column for it.
 *
 * **Shape here, values in the service.** This schema fixes the JSON shape and rejects unknown
 * properties. It deliberately does **not** enumerate attribute vocabularies, because FRD-FIN-016
 * requires a data problem to be reported as a *row-level rejection with its reason*, not as a
 * transport error for the whole file. So every attribute is an optional string, and completeness,
 * code format, permitted values and hierarchy integrity are all checked per row by the import
 * service, which reports them row by row.
 *
 * The one exception is an unknown property, which is a breach of this contract rather than a data
 * defect, and is refused as a validation error (SDD §10.2).
 */
/** A workbook cell: free text here, validated by the import service. */
const Cell = z.string().max(200);
export const CoaImportRowSchema = z.strictObject({
    // Hierarchy — explicit code/name pairs. The service never derives a parent from a code string
    // (FRD-FIN-003): in the approved COA, L2 `70` sits under L1 `6`.
    l1Code: Cell.optional(),
    l1Name: Cell.optional(),
    l2Code: Cell.optional(),
    l2Name: Cell.optional(),
    l3Code: Cell.optional(),
    l3Name: Cell.optional(),
    l4Code: Cell.optional(),
    l4Name: Cell.optional(),
    // The posting account and its attributes (FRD-FIN-002).
    l5Code: Cell.optional(),
    accountName: Cell.optional(),
    accountType: Cell.optional(),
    financialStatement: Cell.optional(),
    normalBalance: Cell.optional(),
    postingAccount: Cell.optional(),
    capexOpex: Cell.optional(),
    directIndirect: Cell.optional(),
    /** The COA's `Control Account?`. It alone populates the system-controlled designation (CLR-12b). */
    controlAccount: Cell.optional(),
    reconciliationRequired: Cell.optional(),
    vatApplicable: Cell.optional(),
    vatTreatment: Cell.optional(),
    module: Cell.optional(),
    costCenterRequired: Cell.optional(),
    projectRequired: Cell.optional(),
    clientRequired: Cell.optional(),
    supplierRequired: Cell.optional(),
    employeeRequired: Cell.optional(),
    assetRequired: Cell.optional(),
    description: Cell.optional(),
    status: Cell.optional(),
    effectiveDate: Cell.optional(),
    remarks: Cell.optional(),
});
/**
 * The permitted values the approved workbook supplies on its `Validation Lists` sheet, travelling
 * with the payload they validate (FRD-FIN-015: "permitted attribute values against the COA
 * validation lists").
 *
 * They are **not stored**: a validation list is neither governed configuration nor a pick-list
 * (MD-13), and the COA is authoritative Finance reference data, not application configuration.
 *
 * Only the ten attributes the workbook actually lists appear here. `Module`, `VAT Treatment` and
 * the six `… Required?` flags are absent because the approved workbook supplies no list for them;
 * no vocabulary is invented for them here or anywhere else.
 */
const ListSchema = z.array(z.string().min(1).max(200)).min(1).max(100);
export const CoaValidationListsSchema = z.strictObject({
    accountType: ListSchema.optional(),
    financialStatement: ListSchema.optional(),
    normalBalance: ListSchema.optional(),
    capexOpex: ListSchema.optional(),
    directIndirect: ListSchema.optional(),
    status: ListSchema.optional(),
    postingAccount: ListSchema.optional(),
    controlAccount: ListSchema.optional(),
    reconciliationRequired: ListSchema.optional(),
    vatApplicable: ListSchema.optional(),
});
export const CoaImportRequestSchema = z.strictObject({
    /** True validates and persists nothing; false validates and applies the accepted rows. */
    dryRun: z.boolean(),
    validationLists: CoaValidationListsSchema.optional(),
    rows: z.array(CoaImportRowSchema).min(1).max(2000),
});
export const CoaImportRejectionSchema = z.object({
    /** 1-based position in `rows`, so a caller can find the row in its source. */
    rowNumber: z.number().int().positive(),
    /** The row's `l5Code` where it supplied a usable one, otherwise null. */
    accountCode: z.string().nullable(),
    reasons: z.array(z.object({ rule: z.string(), field: z.string().nullable(), message: z.string() })),
});
/** The outcome report required by FRD-FIN-016 and FRD-MIG-024. */
export const CoaImportOutcomeSchema = z.object({
    dryRun: z.boolean(),
    accepted: z.number().int().nonnegative(),
    rejected: z.number().int().nonnegative(),
    groupsCreated: z.number().int().nonnegative(),
    groupsReused: z.number().int().nonnegative(),
    accountsCreated: z.number().int().nonnegative(),
    accountsRevised: z.number().int().nonnegative(),
    accountsUnchanged: z.number().int().nonnegative(),
    rejections: z.array(CoaImportRejectionSchema),
});
/**
 * Posting Account Mapping (ERD §5.14, MD-19): the account for a posting purpose where no more
 * specific mapping gives one. Two key fields and a status — no selection criterion, no effective
 * dating, no conditions. The criterion for choosing between alternative approved accounts for one
 * purpose is an open Finance decision (CLR-33) and is deliberately absent here.
 */
export const PostingAccountMappingSchema = z.object({
    id: IdSchema,
    transactionType: z.string(),
    postingPurpose: z.string(),
    accountId: IdSchema,
    accountCode: z.string(),
    status: z.string(),
    version: z.number().int(),
});
const MappingKeySchema = z.string().min(1).max(100);
export const CreatePostingAccountMappingRequestSchema = z.strictObject({
    /** For example customer invoice, supplier invoice, payment, expense claim. */
    transactionType: MappingKeySchema,
    /** The role the account plays: receivable, payable, customer advance, supplier advance. */
    postingPurpose: MappingKeySchema,
    accountId: IdSchema,
});
/** Status is the only mutable attribute: the key and the account are not amended in place. */
export const UpdatePostingAccountMappingRequestSchema = z.strictObject({
    status: z.enum(['active', 'inactive']),
});
/** Journals (FRD-FIN-039 to FIN-049; SDD §8.6, §8.9): posted and draft, from every source. */
export const JournalListRowSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    postingDate: z.string(),
    status: z.string(),
    sourceType: z.string(),
    journalKind: z.string(),
    sourceRecordType: z.string().nullable(),
    sourceRecordId: IdSchema.nullable(),
    originalJournalId: IdSchema.nullable(),
    reference: z.string().nullable(),
    description: z.string().nullable(),
    postedAt: z.string().nullable(),
    postedBy: IdSchema.nullable(),
    createdBy: IdSchema.nullable(),
    adjustmentReason: z.string().nullable(),
    /** not_submitted, submitted, approved, rejected or returned (FRD-FIN-046). */
    approvalStatus: z.string(),
    version: z.number().int(),
    accountingPeriodId: IdSchema,
    accountingPeriodCode: z.string(),
    totalDebit: z.string(),
    totalCredit: z.string(),
});
const JournalAmount = z
    .string()
    .regex(/^(\d{1,17}(\.\d{1,2})?)?$/, 'must be a decimal amount with at most 2 decimals');
/** One manual journal line: an account, one side, and the dimensions its COA flags require. */
export const ManualJournalLineRequestSchema = z.strictObject({
    accountId: IdSchema,
    debit: JournalAmount,
    credit: JournalAmount,
    costCenterId: IdSchema.optional(),
    projectId: IdSchema.optional(),
    clientId: IdSchema.optional(),
    supplierId: IdSchema.optional(),
    employeeId: IdSchema.optional(),
    assetId: IdSchema.optional(),
    locationId: IdSchema.optional(),
    description: z.string().max(500).optional(),
});
/**
 * A manual journal draft (FRD-FIN-040, FIN-043): a standard journal, an adjustment of a posted
 * journal (FRD-FIN-044), or a controlled adjustment with its reason (FRD-FIN-054).
 */
export const ManualJournalRequestSchema = z.strictObject({
    journalKind: z.enum(['standard', 'adjustment', 'controlled_adjustment']),
    postingDate: BusinessDate,
    originalJournalId: IdSchema.optional(),
    adjustmentReason: z.string().max(500).optional(),
    reference: z.string().max(100).optional(),
    description: z.string().max(500).optional(),
    lines: z.array(ManualJournalLineRequestSchema).min(2).max(500),
});
/** A reversal of a posted manual journal, with its own date and reason (FRD-FIN-045). */
export const ReverseJournalRequestSchema = z.strictObject({
    postingDate: BusinessDate,
    reason: z.string().trim().min(1).max(500),
});
const JournalReferenceSchema = z.object({
    id: IdSchema,
    documentNo: z.string(),
    journalKind: z.string(),
});
export const JournalLineSchema = z.object({
    id: IdSchema,
    lineNo: z.number().int(),
    accountId: IdSchema,
    accountCode: z.string(),
    accountName: z.string(),
    debit: z.string(),
    credit: z.string(),
    costCenterId: IdSchema.nullable(),
    projectId: IdSchema.nullable(),
    clientId: IdSchema.nullable(),
    supplierId: IdSchema.nullable(),
    employeeId: IdSchema.nullable(),
    assetId: IdSchema.nullable(),
    locationId: IdSchema.nullable(),
    sourceLine: z.object({ recordType: z.string(), recordId: IdSchema }).nullable(),
    description: z.string().nullable(),
    supplierSummary: z.string().nullable(),
});
export const JournalDetailSchema = z.object({
    journal: JournalListRowSchema,
    /** Described only where the caller may read the source record type (SDD §8.9). */
    source: z
        .object({
        recordType: z.string(),
        recordId: IdSchema,
        displayName: z.string(),
        summary: z.string().nullable(),
    })
        .nullable(),
    original: JournalReferenceSchema.nullable(),
    related: z.array(JournalReferenceSchema),
    lines: z.array(JournalLineSchema),
});
//# sourceMappingURL=ledger.js.map